import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Palette,
  Sparkles,
  Check,
  Pipette,
  Layers,
  Copy,
  Sliders,
  Compass,
} from 'lucide-react';
import {
  COMPREHENSIVE_COLOR_FAMILIES,
  hexToHsl,
  hslToHex,
  isValidHex,
  generateTulipShadesFromHex,
} from '../../utils/tulipColorUtils';

interface UniversalColorPaletteProps {
  currentColor: string; // current active color id or hex
  onColorSelect: (colorHex: string) => void;
  targetLabel?: string;
  onApplyToAll?: () => void;
}

export const UniversalColorPalette: React.FC<UniversalColorPaletteProps> = ({
  currentColor,
  onColorSelect,
  targetLabel = 'todas las flores',
  onApplyToAll,
}) => {
  // Parse initial color
  const initialHex = currentColor.startsWith('#') ? currentColor : '#f5b000';
  const initialHsl = hexToHsl(initialHex);

  const [hue, setHue] = useState<number>(initialHsl.h);
  const [saturation, setSaturation] = useState<number>(initialHsl.s);
  const [lightness, setLightness] = useState<number>(initialHsl.l);
  const [hexInput, setHexInput] = useState<string>(initialHex);
  const [activeTab, setActiveTab] = useState<'spectrum' | 'catalog'>('spectrum');
  const [selectedFamily, setSelectedFamily] = useState<string>('all');
  const [copiedHex, setCopiedHex] = useState(false);

  // Sync state when currentColor prop updates externally
  useEffect(() => {
    if (currentColor && currentColor.startsWith('#')) {
      const parsed = hexToHsl(currentColor);
      setHue(parsed.h);
      setSaturation(parsed.s);
      setLightness(parsed.l);
      setHexInput(currentColor.toUpperCase());
    }
  }, [currentColor]);

  // Derive current calculated Hex
  const calculatedHex = hslToHex(hue, saturation, lightness);

  // When sliders change, notify parent
  const updateFromHsl = useCallback(
    (newH: number, newS: number, newL: number) => {
      setHue(newH);
      setSaturation(newS);
      setLightness(newL);
      const nextHex = hslToHex(newH, newS, newL);
      setHexInput(nextHex.toUpperCase());
      onColorSelect(nextHex);
    },
    [onColorSelect]
  );

  // Handle direct Hex input
  const handleHexInputChange = (val: string) => {
    setHexInput(val);
    if (isValidHex(val)) {
      const formatted = val.startsWith('#') ? val : `#${val}`;
      const { h, s, l } = hexToHsl(formatted);
      setHue(h);
      setSaturation(s);
      setLightness(l);
      onColorSelect(formatted);
    }
  };

  // Eyedropper API if available
  const handlePickFromScreen = async () => {
    if (typeof window !== 'undefined' && 'EyeDropper' in window) {
      try {
        const eyeDropper = new (window as any).EyeDropper();
        const result = await eyeDropper.open();
        if (result?.sRGBHex) {
          handleHexInputChange(result.sRGBHex);
        }
      } catch {
        // User cancelled or not allowed
      }
    }
  };

  // Copy hex
  const handleCopyHex = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(calculatedHex);
      setCopiedHex(true);
      setTimeout(() => setCopiedHex(false), 2000);
    }
  };

  // 2D Spectrum Pad Drag Interaction
  const padRef = useRef<HTMLDivElement>(null);
  const [isDraggingPad, setIsDraggingPad] = useState(false);

  const handlePadInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    if (!padRef.current) return;
    const rect = padRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

    // X axis: Saturation (0% to 100%)
    const newS = Math.round(x * 100);
    // Y axis: Lightness (90% top to 15% bottom)
    const newL = Math.round(92 - y * 74);

    updateFromHsl(hue, newS, newL);
  };

  // Generated shades preview for flower petals
  const shades = generateTulipShadesFromHex(calculatedHex);

  // Filter catalog colors
  const filteredFamilies = selectedFamily === 'all'
    ? COMPREHENSIVE_COLOR_FAMILIES
    : COMPREHENSIVE_COLOR_FAMILIES.filter((f) => f.id === selectedFamily);

  return (
    <div className="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4 text-slate-800">
      {/* HEADER WITH TABS */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-pink-500 via-amber-400 to-sky-500 p-0.5 shadow-md flex items-center justify-center">
            <div className="w-full h-full bg-white rounded-[10px] flex items-center justify-center">
              <Palette className="w-4 h-4 text-pink-600" />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              <span>Paleta Completa de Todos los Colores</span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-200">
                16M+ Tonos
              </span>
            </h4>
            <p className="text-[11px] text-slate-500">
              Espectro continuo visible de 360° y catálogo por familias de colores.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab('spectrum')}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'spectrum'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80 font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Compass className="w-3.5 h-3.5 text-pink-500" />
            <span>Espectro 360°</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('catalog')}
            className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'catalog'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/80 font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Layers className="w-3.5 h-3.5 text-pink-500" />
            <span>Catálogo Completo</span>
          </button>
        </div>
      </div>

      {/* ACTIVE COLOR SPOTLIGHT & PETAL SHADING PREVIEW */}
      <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3.5">
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Main Color Circle with dynamic gradient based on tulip shading */}
          <div
            className="w-12 h-12 rounded-2xl shadow-md border-2 border-white shrink-0 relative flex items-center justify-center transition-transform hover:scale-105"
            style={{
              backgroundColor: calculatedHex,
              backgroundImage: `linear-gradient(135deg, ${shades['--gold-2']}, ${shades['--gold-4']})`,
            }}
          >
            <Sparkles className="w-5 h-5 text-white drop-shadow" />
          </div>

          {/* Color Info */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Color Seleccionado
            </div>
            <div className="text-base font-black font-mono text-slate-900 flex items-center gap-2">
              <span>{calculatedHex.toUpperCase()}</span>
              <button
                type="button"
                onClick={handleCopyHex}
                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/80 rounded transition-colors cursor-pointer"
                title="Copiar código HEX"
              >
                {copiedHex ? (
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
            <div className="text-[10px] text-slate-500 font-mono">
              HSL({hue}°, {saturation}%, {lightness}%)
            </div>
          </div>
        </div>

        {/* Petal Botanical Gradient Swatches (Visual breakdown of 5 depth layers) */}
        <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-[10px] text-slate-500 font-medium mr-1">
            Matiz de Pétalo:
          </span>
          <div
            className="w-4 h-6 rounded-sm border border-slate-200 shadow-xs"
            style={{ backgroundColor: shades['--gold-1'] }}
            title="Luz / Brillo superior"
          />
          <div
            className="w-4 h-6 rounded-sm border border-slate-200 shadow-xs"
            style={{ backgroundColor: shades['--gold-2'] }}
            title="Tono secundario suave"
          />
          <div
            className="w-5 h-7 rounded-md border-2 border-slate-400 shadow-sm scale-105"
            style={{ backgroundColor: shades['--gold-3'] }}
            title="Color base"
          />
          <div
            className="w-4 h-6 rounded-sm border border-slate-200 shadow-xs"
            style={{ backgroundColor: shades['--gold-4'] }}
            title="Sombra de profundidad"
          />
          <div
            className="w-4 h-6 rounded-sm border border-slate-200 shadow-xs"
            style={{ backgroundColor: shades['--gold-5'] }}
            title="Sombra de cáliz y pliegue"
          />
        </div>
      </div>

      {/* TAB 1: 360° CONTINUOUS SPECTRUM CONTROLLER */}
      {activeTab === 'spectrum' && (
        <div className="space-y-4">
          {/* 2D SATURATION & LIGHTNESS PAD */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
              <span className="flex items-center gap-1">
                <Sliders className="w-3.5 h-3.5 text-pink-500" />
                Matriz de Saturación y Brillo
              </span>
              <span className="text-[11px] text-slate-500">
                Arrastra en el recuadro
              </span>
            </div>

            <div
              ref={padRef}
              onMouseDown={(e) => {
                setIsDraggingPad(true);
                handlePadInteraction(e);
              }}
              onMouseMove={(e) => {
                if (isDraggingPad) handlePadInteraction(e);
              }}
              onMouseUp={() => setIsDraggingPad(false)}
              onMouseLeave={() => setIsDraggingPad(false)}
              onTouchStart={(e) => {
                setIsDraggingPad(true);
                handlePadInteraction(e);
              }}
              onTouchMove={(e) => {
                if (isDraggingPad) handlePadInteraction(e);
              }}
              onTouchEnd={() => setIsDraggingPad(false)}
              className="relative w-full h-36 sm:h-40 rounded-xl cursor-crosshair overflow-hidden border border-slate-300 shadow-inner select-none touch-none"
              style={{
                backgroundColor: `hsl(${hue}, 100%, 50%)`,
                backgroundImage: `
                  linear-gradient(to right, #ffffff, transparent),
                  linear-gradient(to top, #000000, transparent)
                `,
              }}
            >
              {/* Target Crosshair Pointer */}
              <div
                className="absolute w-5 h-5 rounded-full border-2 border-white shadow-[0_0_8px_rgba(0,0,0,0.8)] -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-transform"
                style={{
                  left: `${saturation}%`,
                  top: `${Math.max(0, Math.min(100, ((92 - lightness) / 74) * 100))}%`,
                  backgroundColor: calculatedHex,
                }}
              />
            </div>
          </div>

          {/* SLIDER 1: HUE 360° RAINBOW SPECTRUM */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
              <span>Arcoíris Completo (Tono 0° - 360°)</span>
              <span className="font-mono text-[11px] text-amber-600 font-bold">{hue}°</span>
            </div>
            <div className="relative flex items-center">
              <input
                type="range"
                min="0"
                max="360"
                value={hue}
                onChange={(e) => updateFromHsl(Number(e.target.value), saturation, lightness)}
                className="w-full h-5 rounded-full appearance-none cursor-pointer shadow-xs focus:outline-none"
                style={{
                  background:
                    'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
                }}
              />
            </div>
          </div>

          {/* SLIDER 2: SATURATION */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
              <span>Saturación (Gris a Neón Puro)</span>
              <span className="font-mono text-[11px] text-pink-600 font-bold">{saturation}%</span>
            </div>
            <div className="relative flex items-center">
              <input
                type="range"
                min="0"
                max="100"
                value={saturation}
                onChange={(e) => updateFromHsl(hue, Number(e.target.value), lightness)}
                className="w-full h-4 rounded-full appearance-none cursor-pointer shadow-xs focus:outline-none"
                style={{
                  background: `linear-gradient(to right, hsl(${hue}, 0%, ${lightness}%), hsl(${hue}, 100%, ${lightness}%))`,
                }}
              />
            </div>
          </div>

          {/* SLIDER 3: LIGHTNESS / BRIGHTNESS */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
              <span>Luminosidad / Tono (Oscuro a Radiante)</span>
              <span className="font-mono text-[11px] text-sky-600 font-bold">{lightness}%</span>
            </div>
            <div className="relative flex items-center">
              <input
                type="range"
                min="12"
                max="94"
                value={lightness}
                onChange={(e) => updateFromHsl(hue, saturation, Number(e.target.value))}
                className="w-full h-4 rounded-full appearance-none cursor-pointer shadow-xs focus:outline-none"
                style={{
                  background: `linear-gradient(to right, hsl(${hue}, ${saturation}%, 15%), hsl(${hue}, ${saturation}%, 50%), hsl(${hue}, ${saturation}%, 94%))`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: COMPREHENSIVE CATALOG BY COLOR FAMILIES */}
      {activeTab === 'catalog' && (
        <div className="space-y-3">
          {/* Family Filter Chips */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-thin">
            <button
              type="button"
              onClick={() => setSelectedFamily('all')}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer border ${
                selectedFamily === 'all'
                  ? 'bg-pink-600 border-pink-600 text-white shadow-2xs'
                  : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
              }`}
            >
              🌈 Todos (72)
            </button>
            {COMPREHENSIVE_COLOR_FAMILIES.map((family) => (
              <button
                key={family.id}
                type="button"
                onClick={() => setSelectedFamily(family.id)}
                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer border flex items-center gap-1 ${
                  selectedFamily === family.id
                    ? 'bg-pink-600 border-pink-600 text-white shadow-2xs'
                    : 'bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span>{family.emoji}</span>
                <span>{family.name}</span>
              </button>
            ))}
          </div>

          {/* Grid of Colors by Family */}
          <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
            {filteredFamilies.map((family) => (
              <div key={family.id} className="space-y-1.5">
                <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                  <span>{family.emoji}</span>
                  <span>{family.name}</span>
                </div>
                <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-2">
                  {family.colors.map((c) => {
                    const isSelected = calculatedHex.toLowerCase() === c.hex.toLowerCase();
                    return (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => handleHexInputChange(c.hex)}
                        className={`p-2 rounded-xl border text-left transition-all cursor-pointer flex items-center gap-2 group ${
                          isSelected
                            ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-300 shadow-xs'
                            : 'bg-slate-50/80 border-slate-200 hover:bg-white hover:border-slate-300'
                        }`}
                      >
                        <div
                          className="w-6 h-6 rounded-full border border-slate-300 shrink-0 shadow-xs transition-transform group-hover:scale-110 flex items-center justify-center"
                          style={{ backgroundColor: c.hex }}
                        >
                          {isSelected && <Check className="w-3.5 h-3.5 text-white stroke-[3] drop-shadow" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold text-slate-800 truncate group-hover:text-amber-600">
                            {c.name}
                          </div>
                          <div className="text-[10px] font-mono text-slate-500 uppercase">
                            {c.hex}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BOTTOM ACTION & DIRECT HEX INPUT BAR */}
      <div className="pt-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
        {/* Direct Hex Input & Eyedropper */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-300 shadow-inner">
            <span className="text-xs font-bold text-slate-500">#</span>
            <input
              type="text"
              value={hexInput.replace('#', '')}
              onChange={(e) => handleHexInputChange(`#${e.target.value}`)}
              placeholder="F43F5E"
              maxLength={6}
              className="w-20 bg-transparent text-xs font-mono font-bold text-slate-900 border-none focus:outline-none uppercase"
            />
          </div>

          {/* Eyedropper Button if supported */}
          {typeof window !== 'undefined' && 'EyeDropper' in window && (
            <button
              type="button"
              onClick={handlePickFromScreen}
              className="p-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-xl transition-colors cursor-pointer text-slate-700 hover:text-slate-900"
              title="Gotero: Copiar color de cualquier parte de la pantalla"
            >
              <Pipette className="w-4 h-4" />
            </button>
          )}

          {/* Quick presets row */}
          <div className="hidden xs:flex items-center gap-1.5 ml-1">
            {['#f50057', '#eb2f96', '#fa8c16', '#f5b000', '#10b981', '#0284c7', '#9333ea', '#ffffff'].map(
              (quickHex) => (
                <button
                  key={quickHex}
                  type="button"
                  onClick={() => handleHexInputChange(quickHex)}
                  className="w-5 h-5 rounded-full border border-slate-300 shadow-xs transition-transform hover:scale-125 cursor-pointer"
                  style={{ backgroundColor: quickHex }}
                  title={quickHex}
                />
              )
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 ml-auto">
          {onApplyToAll && (
            <button
              type="button"
              onClick={onApplyToAll}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 font-bold text-xs rounded-xl shadow-2xs transition-all cursor-pointer hover:scale-102 active:scale-98 flex items-center gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              <span>Aplicar a todas las flores</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => onColorSelect(calculatedHex)}
            className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer hover:scale-102 active:scale-98 flex items-center gap-1.5"
          >
            <Check className="w-4 h-4 stroke-[2.5]" />
            <span>Aplicar a {targetLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
